import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Stack,
  DefaultPalette,
  Icon,
  TooltipHost,
  Label,
} from "@fluentui/react";
import "../css/styles.css";
import "../scss/NavBar.scss";
import { TeamsFxContext } from "./Context";
import { useTheme } from "../theme/OAS365Theme";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { hasChannelIdentifiersForApi } from "../redux/api/channelAPI";
import { useChannel } from "../redux/hooks/useChannel";
import { useLogin } from "../redux/hooks/useLogin";
import { useMetadata } from "../redux/hooks/useMetadata";
import { useIfsUser } from "../redux/hooks/useIfsUser";
import { useTools } from "../redux/hooks/useTools";
import { useCheckin } from "../redux/hooks/useCheckin";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Loader from "./LoadSpinner";
import { useActivePage } from "../redux/hooks/useActivePage";

import logicalUnitImage from "../assets/luError.png";
import noContentIcon from "../assets/noContentIcon.png";
import channelConfigIcon from "../assets/channelConfigIcon.png";
import documentationHelpIcon from "../assets/documentationHelpIcon.svg";
import {
  PROGRESS_STATUS,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  DATA_LOAD_STATUS,
} from "../redux/constants/metaData";
import AdvSearchObjectConnectionTab from "./checkinDocuments/AdvSearchObjectConnectionTab";

import {
  IFS_SERVER_ERROR,
  LU_FEILD_FAILD_MAIN,
  LU_FEILD_FAILD_SUB,
  IFS_AUTHENTICATE_FAILED,
  IFS_TOKEN_EXPIRED,
  PLEASE_WAIT,
  SUCCESS_TYPE,
  CONFIGURATION_OR_OPERATOR_INFO_TOOLTIP,
} from "../redux/constants/messageTypes";

const CONFIG_DIALOG_OPEN_EVENT = "oas:open-config-dialog";

const SidebarBottomFeature = ({
  dialogHost = false,
  hideLinks = false,
  collapsed = false,
}) => {
  const { teamsContext, onLogout } = useLogin();
  const {
    onChannelConfigLoad,
    onSaveChannelConfiguration,
    channelObjectList = [],
    channelConfigLoad = null,
    channelUpdateStatus = PROGRESS_STATUS.onStart,
    environmentName = "",
    userEmail = "",
    isPrimaryChannel = false,
  } = useChannel();
  const { onObjectConnectionsLoad, selectedObjectConnections } = useCheckin();
  const {
    onSelectedObjectConnectionChanged,
    onSuccessMessageInvoke,
    toggleSideBar,
  } = useTools();
  const { ifsUser } = useIfsUser();
  const { messageBoxInfo = null, logicalUnit, objectConnectionDataLoad } = useMetadata();
  const activePage = useActivePage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isConnectionInitialLoadRef = useRef(false);
  const channelUpdateTerminalHandledRef = useRef(null);

  useEffect(() => {
    // Avoid infinite loops: calling success message dispatchers can update state
    // and re-trigger this effect if dependencies are too broad.
    if (
      channelUpdateStatus === PROGRESS_STATUS.onStart ||
      channelUpdateStatus === PROGRESS_STATUS.onLoading
    ) {
      channelUpdateTerminalHandledRef.current = null;
      return;
    }

    if (
      channelUpdateStatus === PROGRESS_STATUS.onCompletion &&
      channelUpdateTerminalHandledRef.current !== "completion"
    ) {
      channelUpdateTerminalHandledRef.current = "completion";
      onSuccessMessageInvoke({
        type: SUCCESS_TYPE,
        title: "Channel Updated",
        message: "Configurations updated successfully.",
      });
      setIsDialogOpen(false);
      return;
    }

    if (
      channelUpdateStatus === PROGRESS_STATUS.onError &&
      channelUpdateTerminalHandledRef.current !== "error"
    ) {
      channelUpdateTerminalHandledRef.current = "error";
      // Server/validation text is shown via ON_SERVER_ERROR_MESSAGE_INVOKE → MessageBox toast (see channelThunks).
    }
  }, [channelUpdateStatus, onSuccessMessageInvoke]);
  const [isLogoutInProgress, setIsLogoutInProgress] = useState(false);
  const { themeString } = useContext(TeamsFxContext);
  const theme = useTheme();
  const [dataRequested, setDataRequested] = useState(false);
  const [isAddObjectConnectionDialogOpen, setIsAddObjectConnectionDialogOpen] =
    useState(false);
  const requestInFlightRef = useRef(false);
  const [localChannelObjectList, setLocalChannelObjectList] = useState([]);
  const [warningMessage, setWarningMessage] = useState("");
  const MAX_OBJECT_CONNECTIONS = 15;
  const hasChannelScope = hasChannelIdentifiersForApi(teamsContext);
  const isGeneralAppContext = !hasChannelScope;
  const membershipType = teamsContext?.channel?.membershipType;
  const isUnsupportedChannel = false;

  useEffect(() => {
    setLocalChannelObjectList(channelObjectList || []);
  }, [channelObjectList]);

  useEffect(() => {
    if (isAddObjectConnectionDialogOpen && isConnectionInitialLoadRef.current) {
      if (selectedObjectConnections && selectedObjectConnections.length > 0) {
        // Clear any "default" connections loaded by onObjectConnectionsLoad
        onSelectedObjectConnectionChanged([], []);
        // Don't reset ref yet — the initial load may still be in-flight.
        // If we reset here, the initial load response (onObjectConnectionsLoad)
        // will bypass block 1 and enter block 2, adding default connections.
        // The ref is reset only when the initial load actually finishes (else-if below)
        // or when the add dialog closes.
        return;
      } else if (
        objectConnectionDataLoad === DATA_LOAD_STATUS.completed ||
        objectConnectionDataLoad === DATA_LOAD_STATUS.error ||
        objectConnectionDataLoad === DATA_LOAD_STATUS.noDate
      ) {
        // Initial load finished but no connections found or an error occurred.
        // Reset the ref so future (manual) selections aren't swallowed.
        isConnectionInitialLoadRef.current = false;
      }
    }

    if (
      isAddObjectConnectionDialogOpen &&
      selectedObjectConnections &&
      selectedObjectConnections.length > 0 &&
      !isConnectionInitialLoadRef.current
    ) {
      // Filter out items that already exist in the list based on LuName and KeyRef
      const filteredConns = selectedObjectConnections.filter(
        (sel) =>
          !localChannelObjectList.some(
            (existing) =>
              existing.luName === sel.luName && existing.keyRef === sel.keyRef,
          ),
      );

      if (filteredConns.length > 0) {
        setLocalChannelObjectList((prev) => {
          const currentCount = prev.length;
          const available = MAX_OBJECT_CONNECTIONS - currentCount;
          if (available <= 0) {
            setTimeout(() => {
              setWarningMessage(`You can only add up to ${MAX_OBJECT_CONNECTIONS} object connections.`);
            }, 0);
            return prev;
          }
          const sliceCount = Math.min(filteredConns.length, available);
          const newConns = filteredConns.slice(0, sliceCount).map((sel) => ({
            nameDesc: sel.nameDesc || sel.luName,
            keyDesc: sel.keyDesc || sel.keyRef,
            luName: sel.luName,
            keyRef: sel.keyRef,
            docObjDesc: sel.docObjDesc || "",
          }));
          const newTotal = currentCount + sliceCount;
          if (filteredConns.length > available) {
            setTimeout(() => {
              setWarningMessage(`Only ${available} of ${filteredConns.length} selected connections were added. Maximum is ${MAX_OBJECT_CONNECTIONS}.`);
            }, 0);
          } else if (newTotal === MAX_OBJECT_CONNECTIONS) {
            setTimeout(() => {
              onSuccessMessageInvoke({
                type: SUCCESS_TYPE,
                title: "Limit Reached",
                message: `You have reached the maximum of ${MAX_OBJECT_CONNECTIONS} object connections.`,
              });
            }, 0);
          }
          return [...prev, ...newConns];
        });
      }

      // Clear selection in Redux so it doesn't trigger again immediately
      onSelectedObjectConnectionChanged([], []);
      setIsAddObjectConnectionDialogOpen(false);
    }
  }, [
    selectedObjectConnections,
    isAddObjectConnectionDialogOpen,
    onSelectedObjectConnectionChanged,
    localChannelObjectList,
    objectConnectionDataLoad,
  ]);

  useEffect(() => {
    if (!isAddObjectConnectionDialogOpen) {
      isConnectionInitialLoadRef.current = false;
    }
  }, [isAddObjectConnectionDialogOpen]);

  const handleRemoveObjectConnection = (index) => {
    setLocalChannelObjectList((prev) => prev.filter((_, i) => i !== index));
    // TODO: Add API call to remove the object connection
  };

  const version = process.env.REACT_APP_VERSION;
  const isUrlLike = (value) => /^https?:\/\//i.test(String(value || "").trim());
  const tryBuildUrlFromEnvCode = (value) => {
    const raw = String(value || "").trim();
    // Example 1: RND02-25R2-DEV -> https://rnd02-dev.addovation.com
    const withEnv = raw.match(/^([A-Za-z0-9]+)-\d+R\d+-([A-Za-z]+)$/);
    if (withEnv) {
      const site = withEnv[1].toLowerCase();
      const env = withEnv[2].toLowerCase();
      return `https://${site}-${env}.addovation.com`;
    }

    // Example 2: RND02-25R2 -> default to DEV suffix for QA style codes.
    const noEnv = raw.match(/^([A-Za-z0-9]+)-\d+R\d+$/);
    if (noEnv) {
      const site = noEnv[1].toLowerCase();
      return `https://${site}-dev.addovation.com`;
    }

    return "";
  };
  const toConnectedUrlLabel = (...candidates) => {
    const valid = candidates.map((c) => String(c || "").trim()).filter(Boolean);
    const directUrl = valid.find(isUrlLike);
    if (directUrl) return directUrl;
    const fromCode = valid.map(tryBuildUrlFromEnvCode).find(Boolean);
    if (fromCode) return fromCode;
    return valid[0] || "";
  };
  const deriveIfsEndpointFromTokenEndpoint = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const marker = "/auth/realms/";
    const markerIndex = raw.toLowerCase().indexOf(marker);
    if (markerIndex > 0) {
      return raw.slice(0, markerIndex).replace(/\/+$/, "");
    }
    if (isUrlLike(raw)) {
      try {
        const parsed = new URL(raw);
        return `${parsed.protocol}//${parsed.host}`;
      } catch (_) {
        return "";
      }
    }
    return "";
  };

  const persistedConnectedLabel =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("connectedEnvironmentLabel")) ||
    "";
  const storedIfsTokenEndpoint =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("ifsTokenEndPoint")) ||
    "";
  const derivedIfsEndpointFromToken =
    deriveIfsEndpointFromTokenEndpoint(storedIfsTokenEndpoint);
  const storedIfsEndpoint =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("ifsEndpoint")) ||
    "";
  const fallbackEnvironmentName =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("environment")) ||
    "";
  const connectedEnvironmentLabel = toConnectedUrlLabel(
    derivedIfsEndpointFromToken,
    storedIfsEndpoint,
    environmentName,
    persistedConnectedLabel,
    fallbackEnvironmentName,
  );
  const connectedEnvironmentDisplayLabel =
    connectedEnvironmentLabel || "Not connected";
  const normalizeEmailLabel = (value) => {
    const text = String(value || "").trim();
    return text.includes("@") ? text.toLowerCase() : text;
  };
  const normalizeIdentity = (value) => String(value || "").trim().toLowerCase();
  const persistedOfficeEmail =
    (typeof localStorage !== "undefined" && localStorage.getItem("userEmail")) ||
    "";
  const officeUserCandidates = [
    persistedOfficeEmail,
    userEmail,
    teamsContext?.user?.userPrincipalName,
    teamsContext?.user?.loginHint,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const ifsUserCandidates = [
    ifsUser?.UserEmail,
    ifsUser?.userEmail,
    ifsUser?.WebUser,
    ifsUser?.webUser,
    ifsUser?.PreferredUsername,
    ifsUser?.preferredUsername,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const ifsUserWebUser = ifsUserCandidates[0] || "";
  const matchingOfficeUser = officeUserCandidates.find((candidate) => {
    const normalizedCandidate = normalizeIdentity(candidate);
    return ifsUserCandidates.some(
      (ifsCandidate) => normalizeIdentity(ifsCandidate) === normalizedCandidate,
    );
  });
  const officeUserLabel = matchingOfficeUser || officeUserCandidates[0] || "";
  const officeUserDisplayLabel = normalizeEmailLabel(officeUserLabel);
  const ifsUserDisplayLabel = normalizeEmailLabel(ifsUserWebUser);
  const usersMatch =
    !!officeUserLabel &&
    ifsUserCandidates.some(
      (ifsCandidate) =>
        normalizeIdentity(ifsCandidate) === normalizeIdentity(officeUserLabel),
    );
  const shouldShowIfsUser =
    !!officeUserLabel && !!ifsUserWebUser && !usersMatch;

  useEffect(() => {
    if (!environmentName) return;
    if (typeof localStorage === "undefined") return;
    localStorage.setItem("connectedEnvironmentLabel", environmentName);
  }, [environmentName]);

  useEffect(() => {
    if (!isDialogOpen) {
      setDataRequested(false);
      requestInFlightRef.current = false;
    }
  }, [isDialogOpen]);

  useEffect(() => {
    if (!dialogHost) {
      return undefined;
    }
    const handleOpenConfigDialog = () => {
      setIsDialogOpen(true);
    };
    window.addEventListener(CONFIG_DIALOG_OPEN_EVENT, handleOpenConfigDialog);
    return () => window.removeEventListener(CONFIG_DIALOG_OPEN_EVENT, handleOpenConfigDialog);
  }, [dialogHost]);

  const isChanged = React.useMemo(() => {
    if (localChannelObjectList.length !== (channelObjectList || []).length)
      return true;
    return (
      JSON.stringify(localChannelObjectList) !==
      JSON.stringify(channelObjectList || [])
    );
  }, [localChannelObjectList, channelObjectList]);

  useEffect(() => {
    if (
      !isDialogOpen ||
      !teamsContext ||
      !hasChannelScope ||
      dataRequested ||
      requestInFlightRef.current
    )
      return;
    requestInFlightRef.current = true;
    setDataRequested(true);
    onChannelConfigLoad(teamsContext);
  }, [isDialogOpen, teamsContext, hasChannelScope, onChannelConfigLoad, dataRequested]);

  const isDarkTheme = themeString === "dark";
  const backgroundColor = isDarkTheme
    ? theme.colors.backgroundColor
    : DefaultPalette.white;

  const borderStyleLu = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
    width: "100%",
    boxSizing: "border-box",
  };

  const messageBorderStyleLu = {
    textAlign: "center",
    width: "100%",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const mainMessageLableStyleLu = {
    fontWeight: "bold",
    fontSize: theme.fontSizes.normal,
    fontFamily: "Poppins, sans-serif",
    color: theme.colors.fontColor,
    textAlign: "center",
    marginBottom: "10px",
  };

  const subMessageLableStyleLu = {
    fontSize: theme.fontSizes.menusize,
    fontFamily: "Poppins, sans-serif",
    fontWeight: "normal",
    marginBottom: "30px",
    padding: "0px",
    maxWidth: "90%",
    color: theme.colors.fontColor,
    textAlign: "center",
  };

  const imageContainerStyleLu = {
    width: "100%",
    maxWidth: "min(90vw, 500px)",
    aspectRatio: "1.5",
    display: "flex",
    justifyContent: "center",
  };

  const imageStyleLu = {
    width: "80%",
    height: "80%",
    objectFit: "contain",
    display: "block",
  };

  const uanuthorizedImageStyles = {
    maxWidth: "100%",
    maxHeight: "80%",
    width: "300px",
    height: "300px",
    objectFit: "contain",
    objectPosition: "center",
  };

  const loaderBorderStyle = {
    padding: "10px auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "300px",
  };

  const messageBorderStyle = {
    margin: "auto",
    width: "400px",
    paddingLeft: "0px",
    textAlign: "center",
  };

  const handleChannelConfigureClick = () => {
    if (dialogHost) {
      setIsDialogOpen(true);
      return;
    }
    // In launcher mode, close mobile drawer first, then ask host to open dialog.
    toggleSideBar(true);
    setTimeout(
      () => window.dispatchEvent(new Event(CONFIG_DIALOG_OPEN_EVENT)),
      180,
    );
  };

  const handleSaveChannelConfiguration = () => {
    if (localChannelObjectList.length > MAX_OBJECT_CONNECTIONS) {
      setWarningMessage(`You can only save up to ${MAX_OBJECT_CONNECTIONS} object connections. Remove some and try again.`);
      return;
    }
    const objectConnections = localChannelObjectList.map((row) => ({
      luName: row.luName || row.nameDesc,
      nameDesc: row.nameDesc || "",
      keyRef: row.keyRef || row.keyDesc,
      keyDesc: row.keyDesc || "",
      docObjDesc: row.docObjDesc || "",
    }));
    onSaveChannelConfiguration(teamsContext, objectConnections, activePage);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfigDialogClose = (_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    handleCloseDialog();
  };

  const renderUpdatingContent = () => (
    <DialogContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "300px",
        textAlign: "center",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontFamily: theme.fonts.regular,
          marginBottom: "24px",
          color: theme.colors.fontColor,
        }}
      >
        Updating channel configuration...
      </Typography>
      <Loader />
    </DialogContent>
  );

  const handleAddObjectConnection = () => {
    onSelectedObjectConnectionChanged([], []);
    if (!logicalUnit || logicalUnit.length === 0) {
      isConnectionInitialLoadRef.current = true;
      onObjectConnectionsLoad(teamsContext, {
        retry: 0,
        actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
        refreshResponse: RESPONSE_TYPE.none,
      });
    }
    setIsAddObjectConnectionDialogOpen(true);
  };

  const handleCloseAddObjectConnectionDialog = () => {
    onSelectedObjectConnectionChanged([], []);
    setIsAddObjectConnectionDialogOpen(false);
  };

  const handleLogoutClick = async () => {
    if (isLogoutInProgress) {
      return;
    }

    setIsLogoutInProgress(true);
    try {
      await onLogout();
    } finally {
      setIsLogoutInProgress(false);
    }
  };

  const renderErrorContent = () => {
    const status =
      channelUpdateStatus === PROGRESS_STATUS.onError ||
      channelUpdateStatus === PROGRESS_STATUS.onUnauthorized ||
      channelUpdateStatus === PROGRESS_STATUS.onForbidden
        ? channelUpdateStatus
        : channelConfigLoad;

    switch (status) {
      case PROGRESS_STATUS.onForbidden:
        return (
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              <Label style={mainMessageLableStyleLu}>
                {IFS_AUTHENTICATE_FAILED}
              </Label>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >
                <img
                  src={logicalUnitImage}
                  style={uanuthorizedImageStyles}
                  alt="Authentication failed"
                />
              </div>
            </div>
          </div>
        );
      case PROGRESS_STATUS.onUnauthorized:
        return (
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              <Label style={mainMessageLableStyleLu}>{IFS_TOKEN_EXPIRED}</Label>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >
                <img
                  src={logicalUnitImage}
                  style={uanuthorizedImageStyles}
                  alt="Authentication failed"
                />
              </div>
            </div>
          </div>
        );
      // case PROGRESS_STATUS.noData:
      //   return (
      //     <div style={borderStyleLu}>
      //       <div style={messageBorderStyleLu}>
      //         <Label style={mainMessageLableStyleLu}>
      //           {LU_FEILD_FAILD_MAIN}
      //         </Label>
      //         <Label style={subMessageLableStyleLu}>{LU_FEILD_FAILD_SUB}</Label>
      //         <div style={imageContainerStyleLu}>
      //           <img
      //             src={logicalUnitImage}
      //             style={imageStyleLu}
      //             alt="Update the configuration."
      //           />
      //         </div>
      //       </div>
      //     </div>
      //   );
      case PROGRESS_STATUS.onError:
        const errorMessage = messageBoxInfo?.message || IFS_SERVER_ERROR;
        return (
          <div style={loaderBorderStyle}>
            <div style={messageBorderStyle}>
              <Label style={mainMessageLableStyleLu}>{errorMessage}</Label>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                }}
              >
                <img
                  src={logicalUnitImage}
                  style={uanuthorizedImageStyles}
                  alt="Server error"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderLoadingContent = () => (
    <DialogContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "300px",
        textAlign: "center",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontFamily: theme.fonts.regular,
          marginBottom: "24px",
          color: theme.colors.fontColor,
        }}
      >
        {PLEASE_WAIT}
      </Typography>
      <CircularProgress size={80} thickness={4} sx={{ color: "#249F5D" }} />
    </DialogContent>
  );

  const renderDialogContent = () => {
    if (!isDialogOpen) {
      return null;
    }
    if (!dataRequested) {
      return renderLoadingContent();
    }

    if (channelUpdateStatus === PROGRESS_STATUS.onLoading) {
      return renderUpdatingContent();
    }

    switch (channelConfigLoad) {
      case PROGRESS_STATUS.onLoading:
        return renderLoadingContent();
      case PROGRESS_STATUS.onCompletion:
        return (
          <>
            <DialogContent>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: theme.fonts.regular,
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                  color: theme.colors.fontColor,
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <th align="left" style={{ padding: "8px" }}>
                      Object Type
                    </th>
                    <th align="left" style={{ padding: "8px" }}>
                      Object Key
                    </th>
                    <th align="left" style={{ padding: "8px" }}>
                      Description
                    </th>
                    <th
                      align="center"
                      style={{ padding: "8px", width: "40px" }}
                    >
                      <span
                        onClick={handleAddObjectConnection}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: theme.colors.addovation50Green,
                          color: "white",
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                        }}
                        title="Add Object Connection"
                      >
                        <Icon
                          iconName="Add"
                          style={{ fontSize: "14px", fontWeight: "bold" }}
                        />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {localChannelObjectList &&
                  localChannelObjectList.length > 0 ? (
                    localChannelObjectList.map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: "8px" }}>{row.nameDesc}</td>
                        <td style={{ padding: "8px" }}>{row.keyRef}</td>
                        <td style={{ padding: "8px" }}>{row.docObjDesc}</td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          <span
                            onClick={() => handleRemoveObjectConnection(index)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                            title="Remove"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={theme.colors.addovation70Green}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="15" y1="9" x2="9" y2="15"></line>
                              <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ padding: "20px", textAlign: "center" }}
                      >
                        No configuration data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DialogContent>
          </>
        );
      case PROGRESS_STATUS.noData:
        return (
          <>
            <DialogContent>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: theme.fonts.regular,
                  fontSize: theme.fontSizes.extraMedium,
                  color: theme.colors.fontColor,
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <th align="left" style={{ padding: "8px" }}>
                      Object Type
                    </th>
                    <th align="left" style={{ padding: "8px" }}>
                      Object Key
                    </th>
                    <th align="left" style={{ padding: "8px" }}>
                      Description
                    </th>
                    <th
                      align="center"
                      style={{ padding: "8px", width: "40px" }}
                    >
                      <span
                        onClick={handleAddObjectConnection}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: theme.colors.addovation50Green,
                          color: "white",
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                        }}
                        title="Add Object Connection"
                      >
                        <Icon
                          iconName="Add"
                          style={{ fontSize: "14px", fontWeight: "bold" }}
                        />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {localChannelObjectList &&
                  localChannelObjectList.length > 0 ? (
                    localChannelObjectList.map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: "8px" }}>{row.nameDesc}</td>
                        <td style={{ padding: "8px" }}>{row.keyDesc}</td>
                        <td style={{ padding: "8px" }}>{row.docObjDesc}</td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          <span
                            onClick={() => handleRemoveObjectConnection(index)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                            title="Remove"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={theme.colors.addovation70Green}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="15" y1="9" x2="9" y2="15"></line>
                              <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ padding: "20px", textAlign: "center" }}
                      >
                        No configuration data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DialogContent>
          </>
        );
      default:
        return (
          <DialogContent
            sx={{
              minHeight: "380px",
              padding: "20px",
              marginTop: "30px",
              overflow: "hidden",
            }}
          >
            {renderErrorContent()}
          </DialogContent>
        );
    }
  };

  const renderFooter = () => {
    if (
      channelConfigLoad === PROGRESS_STATUS.onCompletion ||
      channelConfigLoad === PROGRESS_STATUS.noData
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            // With multiple info lines, center alignment makes the Close button look too high.
            // Bottom-align it to the info block for a more balanced layout.
            alignItems: "flex-end",
            padding: "16px",
            paddingTop: "30px",
          }}
        >
          <Box
            sx={{
              color: theme.colors.addovation50Green,
              fontWeight: theme.fontWeight.normal,
              fontSize: "11px",
              fontFamily: theme.fonts.regular,
              lineHeight: 1.5,
            }}
          >
            <div>Version: {version}</div>
            <div>Connected to: {connectedEnvironmentDisplayLabel}</div>
            <div>
              {usersMatch ? "User" : "Office user"}: {officeUserDisplayLabel}
            </div>
            {shouldShowIfsUser && <div>IFS user: {ifsUserDisplayLabel}</div>}
          </Box>

          <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Button
              onClick={handleCloseDialog}
              sx={{
                backgroundColor: theme.colors.backgroundColor,
                color: theme.colors.addovation70Green,
                border: `1px solid ${theme.colors.addovation70Green}`,
                borderRadius: "80px",
                textTransform: "none",
                paddingX: 3,
                height: "40px",
                fontFamily: theme.fonts.regular,
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                "&:hover": {
                  backgroundColor: theme.colors.addovation10Grey,
                },
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleSaveChannelConfiguration}
              disabled={
                channelUpdateStatus === PROGRESS_STATUS.onLoading || !isChanged
              }
              sx={{
                backgroundColor: theme.colors.addovation50Green,
                color: "white",
                fontFamily: theme.fonts?.regular,
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                textTransform: "none",
                borderRadius: "80px",
                paddingX: 3,
                height: "40px",
                "&:hover": {
                  backgroundColor: theme.colors.addovation70Green,
                },
                "&.Mui-disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "16px",
            paddingTop: "8px",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              backgroundColor: theme.colors.backgroundColor,
              color: theme.colors.addovation70Green,
              border: `1px solid ${theme.colors.addovation70Green}`,
              borderRadius: "80px",
              textTransform: "none",
              paddingX: 3,
              height: "40px",
              fontFamily: theme.fonts.regular,
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
              "&:hover": {
                backgroundColor: theme.colors.addovation10Grey,
              },
            }}
          >
            Close
          </Button>
        </Box>
      );
    }
  };

  const renderGeneralAppFooter = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "16px",
        paddingTop: "0px",
        paddingBottom: "20px",
        gap: "18px",
        transform: "translateY(-26px)",
      }}
    >
      <Box
        sx={{
          color: theme.colors.addovation50Green,
          fontWeight: theme.fontWeight.normal,
          fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          fontFamily: theme.fonts.regular,
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        <div>Version: {version}</div>
        <div>Connected to: {connectedEnvironmentDisplayLabel}</div>
        <div>
          {usersMatch ? "User" : "Office user"}: {officeUserDisplayLabel}
        </div>
        {shouldShowIfsUser && <div>IFS user: {ifsUserDisplayLabel}</div>}
      </Box>

      <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <Button
          onClick={handleCloseDialog}
          sx={{
            backgroundColor: theme.colors.addovation50Green,
            color: "white",
            textTransform: "none",
            borderRadius: "16px",
            paddingX: 2,
            minWidth: "72px",
            height: "34px",
            fontFamily: theme.fonts.regular,
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            "&:hover": {
              backgroundColor: theme.colors.addovation70Green,
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {!hideLinks && (
        <Stack
          horizontalAlign={collapsed ? "center" : "start"}
          verticalAlign="end"
          grow
          enableScopedSelectors
          styles={{ root: { background: backgroundColor, height: "100%" } }}
          tokens={{ childrenGap: collapsed ? 4 : 1, padding: collapsed ? 0 : 1 }}
        >
          <Stack.Item>
            <TooltipHost content="Documentation" id="sidebar-doc-link">
              <a
                href="https://docs.addovation.com/services/Share/ShareTeams/ShareTeams.html"
                target="_blank"
                rel="noreferrer"
                aria-label="Documentation"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapsed ? "center" : "flex-start",
                  paddingLeft: collapsed ? 0 : 5,
                  userSelect: "none",
                  paddingBottom: collapsed ? 8 : "15px",
                  textDecoration: "none",
                  width: collapsed ? "100%" : "auto",
                }}
              >
                <img
                  src={documentationHelpIcon}
                  alt="Documentation"
                  style={{
                    width: collapsed ? 18 : "13px",
                    height: collapsed ? 18 : "13px",
                    marginRight: collapsed ? 0 : 3,
                    paddingBottom: collapsed ? 0 : "2px",
                  }}
                />
                {!collapsed && (
                  <span
                    style={{
                      color: theme.colors.addovation50Green,
                      fontWeight: theme.fontWeight.normal,
                      fontSize: theme.fontSizes.normal,
                      fontFamily: theme.fonts.regular,
                    }}
                  >
                    Documentation
                  </span>
                )}
              </a>
            </TooltipHost>
          </Stack.Item>
          <Stack.Item>
            <TooltipHost
              content={isUnsupportedChannel ? "Configuration not available for this channel" : "Configurations"}
              id="sidebar-config-link"
            >
              <div
                onClick={isUnsupportedChannel ? undefined : handleChannelConfigureClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapsed ? "center" : "flex-start",
                  cursor: isUnsupportedChannel ? "not-allowed" : "pointer",
                  paddingLeft: collapsed ? 0 : 5,
                  userSelect: "none",
                  paddingBottom: collapsed ? 8 : "15px",
                  width: collapsed ? "100%" : "auto",
                  opacity: isUnsupportedChannel ? 0.5 : 1,
                }}
                className="channel-configure-link"
                aria-disabled={isUnsupportedChannel}
              >
                <img
                  src={channelConfigIcon}
                  style={{
                    color: theme.colors.addovation50Green,
                    width: collapsed ? 18 : theme.fontSizes.normal,
                    marginRight: collapsed ? 0 : 4,
                    paddingBottom: collapsed ? 0 : "4px",
                  }}
                  alt="channelConfig"
                />
                {!collapsed && (
                  <span
                    style={{
                      color: theme.colors.addovation50Green,
                      fontWeight: theme.fontWeight.normal,
                      fontSize: theme.fontSizes.normal,
                      fontFamily: theme.fonts.regular,
                    }}
                  >
                    Configurations
                  </span>
                )}
              </div>
            </TooltipHost>
          </Stack.Item>
          <Stack.Item>
            <TooltipHost
              content={isLogoutInProgress ? "Signing out..." : "Logout"}
              id="sidebar-logout-link"
            >
              <div
                onClick={handleLogoutClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapsed ? "center" : "flex-start",
                  cursor: isLogoutInProgress ? "default" : "pointer",
                  paddingLeft: collapsed ? 0 : 5,
                  userSelect: "none",
                  paddingBottom: collapsed ? 0 : "10px",
                  opacity: isLogoutInProgress ? 0.6 : 1,
                  width: collapsed ? "100%" : "auto",
                }}
                className="channel-configure-link"
                aria-disabled={isLogoutInProgress}
              >
                <Icon
                  iconName="SignOut"
                  style={{
                    color: theme.colors.addovation50Green,
                    fontSize: collapsed ? 18 : undefined,
                    width: collapsed ? 18 : theme.fontSizes.normal,
                    marginRight: collapsed ? 0 : 4,
                    paddingBottom: collapsed ? 0 : "2px",
                  }}
                />
                {!collapsed && (
                  <span
                    style={{
                      color: theme.colors.addovation50Green,
                      fontWeight: theme.fontWeight.normal,
                      fontSize: theme.fontSizes.normal,
                      fontFamily: theme.fonts.regular,
                    }}
                  >
                    {isLogoutInProgress ? "Signing out..." : "Logout"}
                  </span>
                )}
              </div>
            </TooltipHost>
          </Stack.Item>
        </Stack>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={handleConfigDialogClose}
        disableEscapeKeyDown
        aria-labelledby="channel-config-dialog-title"
        maxWidth="lg"
        sx={{
          zIndex: 2500,
          "& .MuiBackdrop-root": {
            zIndex: 2499,
          },
          "& .MuiDialog-paper": {
            position: "relative",
            zIndex: 2500,
            width: "700px",
            maxWidth: "95vw",
            maxHeight: "90vh",
            borderRadius: "8px",
            borderTop: "6px solid transparent",
            borderImage:
              "linear-gradient(90deg, rgb(36, 159, 93) 0%, rgb(226, 239, 223) 98.79%) 1 / 1 / 0 stretch",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            background: theme.colors.backgroundColor,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontFamily: theme.fonts.regular,
            fontWeight: theme.fontWeight.titleWeight,
            fontSize: theme.fontSizes.medium,
            paddingBottom: "30px",
            color: theme.colors.fontColor,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          Addovation Share - Configuration
          {!isGeneralAppContext && (
            <Tooltip
              title={CONFIGURATION_OR_OPERATOR_INFO_TOOLTIP}
              placement="top"
              arrow
              enterDelay={100}
              slotProps={{
                popper: {
                  sx: { zIndex: 3000 },
                },
                tooltip: {
                  sx: {
                    color: "#ffffff",
                    backgroundColor: theme.colors.addovation70Green,
                    fontFamily: theme.fonts.regular,
                    fontSize: theme.fontSizes.normal,
                  },
                },
                arrow: {
                  sx: { color: theme.colors.addovation70Green },
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "default",
                  lineHeight: 1,
                  verticalAlign: "middle",
                  color: theme.colors.addovation70Green,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "1em", display: "block" }} />
              </Box>
            </Tooltip>
          )}
        </DialogTitle>

        {isGeneralAppContext ? (
          <>
            <DialogContent
              sx={{ minHeight: "36px", paddingBottom: 0, paddingTop: "4px" }}
            />
            {renderGeneralAppFooter()}
          </>
        ) : (
          <>
            {renderDialogContent()}
            {renderFooter()}
          </>
        )}

        {warningMessage && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.3)",
              zIndex: 2600,
              borderRadius: "8px",
            }}
            onClick={() => setWarningMessage("")}
          >
            <Box
              sx={{
                backgroundColor: theme.colors.backgroundColor,
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "400px",
                width: "90%",
                textAlign: "center",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <DialogTitle
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  padding: 0,
                  marginBottom: "16px",
                  color: theme.colors.fontColor,
                  fontFamily: theme.fonts.regular,
                  fontSize: theme.fontSizes.medium,
                }}
              >
                Limit Reached
              </DialogTitle>
              <DialogContent
                sx={{
                  textAlign: "center",
                  padding: 0,
                  marginBottom: "24px",
                  color: theme.colors.fontColor,
                  fontFamily: theme.fonts.regular,
                  fontSize: theme.fontSizes.normal ?? "14px",
                }}
              >
                {warningMessage}
              </DialogContent>
              <DialogActions sx={{ justifyContent: "center", padding: 0 }}>
                <Button
                  variant="contained"
                  onClick={() => setWarningMessage("")}
                  sx={{
                    borderRadius: "25px",
                    color: theme.colors.buttonTextColor,
                    backgroundColor: theme.colors.themeDarkColor,
                    fontFamily: theme.fonts.regular,
                    fontWeight: theme.fontWeight.menuWeight,
                    fontSize: theme.fontSizes.normal ?? "14px",
                    textTransform: "none",
                    padding: "6px 24px",
                  }}
                >
                  OK
                </Button>
              </DialogActions>
            </Box>
          </Box>
        )}
      </Dialog>
      <Dialog
        open={isAddObjectConnectionDialogOpen}
        onClose={handleCloseAddObjectConnectionDialog}
        maxWidth="md"
        sx={{
          zIndex: 2500,
          "& .MuiBackdrop-root": {
            zIndex: 2499,
          },
          "& .MuiDialog-paper": {
            zIndex: 2500,
            width: "100%",
            maxWidth: "95vw",
            maxHeight: "90vh",
            borderRadius: "8px",
            borderTop: "6px solid transparent",
            borderImage:
              "linear-gradient(90deg, rgb(36, 159, 93) 0%, rgb(226, 239, 223) 98.79%) 1 / 1 / 0 stretch",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            background: theme.colors.backgroundColor,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontFamily: theme.fonts.regular,
            fontWeight: theme.fontWeight.titleWeight,
            fontSize: theme.fontSizes.medium,
            paddingBottom: "10px",
            color: theme.colors.fontColor,
          }}
        >
          Add Object Connection
        </DialogTitle>
        <DialogContent sx={{ minHeight: "380px" }}>
          {!logicalUnit || logicalUnit.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="200px"
            >
              <CircularProgress
                sx={{ color: theme.colors.addovation50Green }}
              />
              <Typography
                sx={{
                  marginTop: 2,
                  color: theme.colors.fontColor,
                  fontFamily: theme.fonts.regular,
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                }}
              >
                Loading object types...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <AdvSearchObjectConnectionTab />
            </Box>
          )}
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "16px",
            paddingTop: "30px",
            gap: "10px",
          }}
        >
          <Button
            onClick={handleCloseAddObjectConnectionDialog}
            sx={{
              backgroundColor: "white",
              color: theme.colors.addovation50Green,
              border: `1px solid ${theme.colors.addovation50Green}`,
              fontFamily: theme.fonts?.regular,
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
              textTransform: "none",
              borderRadius: "20px",
              paddingX: 3,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default SidebarBottomFeature;
